import React from 'react';
import Icon from '../../../components/AppIcon';
import { Badge } from '../../../components/ui/Badge';
import { detailsToLines } from '../apiErrors';

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5">
        <Icon name="ShieldCheck" size={12} /> Platform Admin
      </span>
      <h1 className="text-2xl font-bold text-slate-900 mt-2">{title}</h1>
      {subtitle && <p className="text-slate-500 text-sm mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

export const Placeholder = ({ icon = 'Construction', title, text }) => (
  <div className="border border-dashed border-slate-300 rounded-xl bg-slate-50 p-10 text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-200 text-slate-500 mb-3">
      <Icon name={icon} size={22} />
    </div>
    <h3 className="text-slate-700 font-semibold">{title}</h3>
    {text && <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">{text}</p>}
    <span className="inline-block mt-4 text-[11px] font-mono text-slate-400 bg-slate-100 rounded px-2 py-0.5">status: not_implemented</span>
  </div>
);

const statusVariant = { active: 'success', suspended: 'warning', draft: 'secondary', revoked: 'destructive' };
export const StatusBadge = ({ status }) => (
  <Badge variant={statusVariant[status] || 'outline'} className="capitalize">{status}</Badge>
);

const sensVariant = { none: 'outline', pii: 'info', restricted: 'destructive' };
export const SensitivityBadge = ({ sensitivity }) => (
  <Badge variant={sensVariant[sensitivity] || 'outline'} className="uppercase text-[10px] tracking-wide">{sensitivity}</Badge>
);

// Renders a parsed API error (message + field-level detail lines) as a banner.
export const ErrorBanner = ({ error, onClose }) => {
  if (!error) return null;
  const lines = detailsToLines(error.details);
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <div className="flex items-start gap-2">
        <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0 text-red-500" />
        <div className="flex-1 min-w-0">
          <p className="font-medium">{error.message}</p>
          {lines.length > 0 && (
            <ul className="mt-1 list-disc list-inside space-y-0.5">
              {lines.map((l, i) => <li key={i} className="font-mono text-xs break-all">{l}</li>)}
            </ul>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} className="text-red-400 hover:text-red-600 shrink-0"><Icon name="X" size={14} /></button>
        )}
      </div>
    </div>
  );
};

// ---- form / modal primitives (shared by the consumer form + definition wizard) ----
export const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-400';

export const Overlay = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4"
    onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
  >
    {children}
  </div>
);

export const Field = ({ label, required, hint, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

export const ReadOnly = ({ label, value }) => (
  <div>
    <span className="block text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</span>
    <span className="text-slate-700">{value}</span>
  </div>
);

export const FErr = ({ children }) => <p className="text-xs text-red-600 mt-1">{children}</p>;

export const EmptyState = ({ icon = 'Inbox', title, text, action }) => (
  <div className="text-center py-14 border border-slate-200 rounded-xl bg-white">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 text-slate-400 mb-3">
      <Icon name={icon} size={22} />
    </div>
    <h3 className="text-slate-700 font-semibold">{title}</h3>
    {text && <p className="text-slate-500 text-sm mt-1">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
